import { useMemo } from "react";

/**
 * Hook Lead++ : useFilteredProducts
 * Architecture : Filtrage déterministe avec fallback visiteur intégré.
 * Garantit que le flux ne s'interrompt jamais, même sans profil chargé.
 *
 * ============================================================
 * GUIDE MAINTENEUR — EXTENSION TYPE DE LAIT
 * ============================================================
 *
 * Cette version ajoute un 5e argument optionnel : typeLaitActif.
 *
 * Objectif :
 * - brancher le filtre "type de lait" sans casser les appels existants
 * - rester rétrocompatible avec toute signature legacy
 * - n'appliquer le filtre que sur les vues boutique classiques
 *
 * IMPORTANT :
 * - si typeLaitActif est vide, le comportement est inchangé
 * - ce hook ne modifie pas le pricing
 * - ce hook ne modifie pas les structures produit
 */
export const useFilteredProducts = (
  allProducts,
  vueActuelle,
  profilPricing,
  categorieActive = null,
  typeLaitActif = ""
) => {
  return useMemo(() => {
    // 1. Protection initiale (Fail-safe)
    if (!allProducts || !Array.isArray(allProducts)) return [];

    // 2. Fallback Visiteur : On crée un objet safe si profilPricing est null
    const safePricing = profilPricing || {
      type_profil: "visiteur",
      discountRate: 0,
      isAdmin: false,
    };

    // 3. Normalisation légère du filtre lait
    const normalizedMilkType =
      typeof typeLaitActif === "string" ? typeLaitActif.trim() : "";

    // 4. Filtrage "Online" (Sécurité Types PHP : gère boolean, int et string)
    const produitsEnLigne = allProducts.filter((p) => {
      return p.isOnline === true || p.isOnline === 1 || p.isOnline === "1";
    });

    let filtered = [...produitsEnLigne];

    // 5. Dispatcher de Logique par Vue
    switch (vueActuelle) {
      case "actions":
        // Vue Promotions : Uniquement les produits avec remise active
        filtered = filtered.filter(
          (p) =>
            p.isDiscount === true || p.isDiscount === 1 || p.isDiscount === "1"
        );
        break;

      case "magasin":
      case "produits":
        // Filtrage Catégorie (Supporte les deux clés possibles du backend)
        if (categorieActive && categorieActive !== "Toutes") {
          filtered = filtered.filter(
            (p) => (p.category || p.categorie) === categorieActive
          );
        }

        /**
         * ========================================================
         * GUIDE — FILTRE TYPE DE LAIT
         * ========================================================
         *
         * Filtre optionnel et non bloquant.
         * Si aucune valeur active, on ne change rien.
         */
        if (normalizedMilkType) {
          filtered = filtered.filter((p) => {
            const productMilkType =
              typeof p?.type_lait === "string" ? p.type_lait.trim() : "";
            return productMilkType === normalizedMilkType;
          });
        }

        // ✅ Sécurité Lead++ : Filtrage par droit de remise
        // Un produit demandant un discount supérieur au droit de l'user est masqué.
        filtered = filtered.filter(
          (p) => (p.discountRate || 0) <= (safePricing.discountRate || 0)
        );
        break;

      default:
        // Par défaut (ex: accueil), on garde les produits en ligne
        break;
    }

    // 6. Monitoring essentiel (Stratégie Diagnostic Profond)
    console.log(
      `[useFilteredProducts] Pipe: ${allProducts.length} → ${
        produitsEnLigne.length
      } → ${filtered.length} | Mode: ${safePricing.type_profil} | Lait: ${
        normalizedMilkType || "none"
      }`
    );

    return filtered;
  }, [allProducts, vueActuelle, profilPricing, categorieActive, typeLaitActif]);
};
